/**
 * src/audio/AudioEngine.ts
 *
 * Procedural Web Audio API Liturgical Sub-Bass Drone Generator & SFX Engine.
 * Implements IAudioEngine with zero external audio assets, pure real-time synthesis,
 * click-free exponential gain envelopes, and browser autoplay compliance.
 */

export interface IAudioEngine {
  isInitialized: boolean;
  isPlaying: boolean;
  isTransitioning: boolean;
  masterVolume: number;
  init(): Promise<void>;
  toggleDrone(): Promise<boolean>;
  playAnvilStrike(): void;
  playFlameIgnite(): void;
  playCardDraw(): void;
  setMasterVolume(volume: number): void;
  dispose(): void;
}

export type AudioStateListener = (state: {
  isPlaying: boolean;
  isTransitioning: boolean;
  volume: number;
}) => void;

export class AudioEngine implements IAudioEngine {
  private static instance: AudioEngine | null = null;

  public isInitialized: boolean = false;
  public isPlaying: boolean = false;
  public isTransitioning: boolean = false;
  public masterVolume: number = 0.75;

  private ctx: AudioContext | null = null;
  private droneMasterGain: GainNode | null = null;
  private sfxMasterGain: GainNode | null = null;
  private compressor: DynamicsCompressorNode | null = null;

  // Active synthesis nodes for drone
  private droneNodes: {
    oscillators: OscillatorNode[];
    noiseSource: AudioBufferSourceNode | null;
    lfo: OscillatorNode | null;
  } = { oscillators: [], noiseSource: null, lfo: null };

  private stopTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private transitionTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private listeners: Set<AudioStateListener> = new Set();

  private constructor() {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }
  }

  public static getInstance(): AudioEngine {
    if (!AudioEngine.instance) {
      AudioEngine.instance = new AudioEngine();
    }
    return AudioEngine.instance;
  }

  public subscribe(listener: AudioStateListener): () => void {
    this.listeners.add(listener);
    listener({
      isPlaying: this.isPlaying,
      isTransitioning: this.isTransitioning,
      volume: this.masterVolume,
    });
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => {
      listener({
        isPlaying: this.isPlaying,
        isTransitioning: this.isTransitioning,
        volume: this.masterVolume,
      });
    });
  }

  public isAudioContextAvailable(): boolean {
    if (typeof window === 'undefined') return false;
    return !!(
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    );
  }

  public async init(): Promise<void> {
    if (this.isInitialized && this.ctx && this.ctx.state !== 'closed') {
      return;
    }

    if (typeof window === 'undefined') return;

    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;

    if (!AudioContextClass) return;

    this.ctx = new AudioContextClass();

    // 1. Dynamics Compressor for master peak protection
    const now = this.ctx.currentTime;
    this.compressor = this.ctx.createDynamicsCompressor();
    this.compressor.threshold.setValueAtTime(-18, now);
    this.compressor.knee.setValueAtTime(12, now);
    this.compressor.ratio.setValueAtTime(4, now);
    this.compressor.attack.setValueAtTime(0.05, now);
    this.compressor.release.setValueAtTime(0.25, now);
    this.compressor.connect(this.ctx.destination);

    // 2. Drone Master Gain Node (initialized to clamped silence: 0.0001)
    this.droneMasterGain = this.ctx.createGain();
    this.droneMasterGain.gain.setValueAtTime(0.0001, now);
    this.droneMasterGain.connect(this.compressor);

    // 3. SFX Master Gain Node
    this.sfxMasterGain = this.ctx.createGain();
    this.sfxMasterGain.gain.setValueAtTime(this.masterVolume, now);
    this.sfxMasterGain.connect(this.compressor);

    this.isInitialized = true;
  }

  public async toggleDrone(): Promise<boolean> {
    await this.init();

    if (!this.ctx) return false;

    // Autoplay policy unlocking
    if (this.ctx.state === 'suspended') {
      await this.ctx.resume();
    }

    // Re-entrancy guard: ignore toggle requests while a stop transition is actively executing
    if (this.stopTimeoutId !== null) {
      return false;
    }

    if (this.isPlaying) {
      await this.stopDrone();
      return false;
    } else {
      await this.startDrone();
      return true;
    }
  }

  private async startDrone(): Promise<void> {
    if (!this.ctx || !this.droneMasterGain) return;

    // Clear any pending teardown timeouts from previous stops
    if (this.stopTimeoutId !== null) {
      clearTimeout(this.stopTimeoutId);
      this.stopTimeoutId = null;
    }
    if (this.transitionTimeoutId !== null) {
      clearTimeout(this.transitionTimeoutId);
      this.transitionTimeoutId = null;
    }

    this.isTransitioning = true;
    this.notifyListeners();

    const now = this.ctx.currentTime;

    // Clean up any stale nodes if restarting rapidly
    this.teardownDroneNodes();

    // 1. Subterranean Resonant Lowpass Filter
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(160, now);
    filter.Q.setValueAtTime(3.2, now);

    // 2. LFO for slow cavernous respiration (0.065 Hz sine modulation +/- 85 Hz)
    const lfo = this.ctx.createOscillator();
    const lfoGain = this.ctx.createGain();
    lfo.type = 'sine';
    lfo.frequency.setValueAtTime(0.065, now);
    lfoGain.gain.setValueAtTime(85, now);
    lfo.connect(lfoGain);
    lfoGain.connect(filter.frequency);
    lfo.start(now);
    this.droneNodes.lfo = lfo;

    // 3. Dual Sub-Bass Oscillators (55.0 Hz & 55.35 Hz -> 0.35 Hz Delta Binaural Beat)
    const osc1 = this.ctx.createOscillator();
    const g1 = this.ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(55.0, now);
    g1.gain.setValueAtTime(0.60, now);
    osc1.connect(g1);
    g1.connect(filter);

    const osc2 = this.ctx.createOscillator();
    const g2 = this.ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(55.35, now);
    g2.gain.setValueAtTime(0.50, now);
    osc2.connect(g2);
    g2.connect(filter);

    // 4. Harmonic Overtones (110.0 Hz Triangle & 165.0 Hz Sine)
    const osc3 = this.ctx.createOscillator();
    const g3 = this.ctx.createGain();
    osc3.type = 'triangle';
    osc3.frequency.setValueAtTime(110.0, now);
    g3.gain.setValueAtTime(0.15, now);
    osc3.connect(g3);
    g3.connect(filter);

    const osc4 = this.ctx.createOscillator();
    const g4 = this.ctx.createGain();
    osc4.type = 'sine';
    osc4.frequency.setValueAtTime(165.0, now);
    g4.gain.setValueAtTime(0.08, now);
    osc4.connect(g4);
    g4.connect(filter);

    this.droneNodes.oscillators = [osc1, osc2, osc3, osc4];
    this.droneNodes.oscillators.forEach((osc) => osc.start(now));

    // 5. Procedural Brownian Cavern Noise
    const noiseBuffer = this.generateBrownianNoiseBuffer(this.ctx, 5.0);
    const noiseSource = this.ctx.createBufferSource();
    noiseSource.buffer = noiseBuffer;
    noiseSource.loop = true;

    const noiseFilter = this.ctx.createBiquadFilter();
    noiseFilter.type = 'bandpass';
    noiseFilter.frequency.setValueAtTime(110, now);
    noiseFilter.Q.setValueAtTime(1.5, now);

    const noiseGain = this.ctx.createGain();
    noiseGain.gain.setValueAtTime(0.05, now);

    noiseSource.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(filter);
    noiseSource.start(now);
    this.droneNodes.noiseSource = noiseSource;

    // 6. Catacomb Convolver Reverb Routing (78% Dry / 22% Wet)
    const convolver = this.ctx.createConvolver();
    convolver.buffer = this.generateCatacombImpulseResponse(this.ctx, 2.8, 2.5);

    const dryGain = this.ctx.createGain();
    const wetGain = this.ctx.createGain();
    dryGain.gain.setValueAtTime(0.78, now);
    wetGain.gain.setValueAtTime(0.22, now);

    filter.connect(dryGain);
    filter.connect(convolver);
    convolver.connect(wetGain);

    dryGain.connect(this.droneMasterGain);
    wetGain.connect(this.droneMasterGain);

    // 7. Click-Free Attack Ramp: Exponential from 0.0001 to target over 3.0s
    const targetGain = Math.max(0.0001, 0.70 * this.masterVolume);
    this.droneMasterGain.gain.cancelScheduledValues(now);
    this.droneMasterGain.gain.setValueAtTime(0.0001, now);
    this.droneMasterGain.gain.exponentialRampToValueAtTime(targetGain, now + 3.0);

    this.isPlaying = true;
    this.notifyListeners();

    this.transitionTimeoutId = setTimeout(() => {
      this.isTransitioning = false;
      this.transitionTimeoutId = null;
      this.notifyListeners();
    }, 3000);
  }

  private async stopDrone(): Promise<void> {
    if (!this.ctx || !this.droneMasterGain) return;

    // Re-entrancy guard: if already stopping, do not restart or reschedule
    if (this.stopTimeoutId !== null) return;

    // Clear any pending attack transition timer
    if (this.transitionTimeoutId !== null) {
      clearTimeout(this.transitionTimeoutId);
      this.transitionTimeoutId = null;
    }

    this.isTransitioning = true;
    this.notifyListeners();

    const now = this.ctx.currentTime;
    const currentGain = Math.max(0.0001, this.droneMasterGain.gain.value);

    // Click-Free Release Ramp: Exponential from current value down to 0.0001 over 2.5s
    this.droneMasterGain.gain.cancelScheduledValues(now);
    this.droneMasterGain.gain.setValueAtTime(currentGain, now);
    this.droneMasterGain.gain.exponentialRampToValueAtTime(0.0001, now + 2.5);

    this.stopTimeoutId = setTimeout(() => {
      this.teardownDroneNodes();
      this.isPlaying = false;
      this.isTransitioning = false;
      this.stopTimeoutId = null;
      this.notifyListeners();
    }, 2550);
  }

  private teardownDroneNodes(): void {
    this.droneNodes.oscillators.forEach((osc) => {
      try {
        osc.stop();
        osc.disconnect();
      } catch {
        // Node may already be stopped
      }
    });
    if (this.droneNodes.noiseSource) {
      try {
        this.droneNodes.noiseSource.stop();
        this.droneNodes.noiseSource.disconnect();
      } catch {}
    }
    if (this.droneNodes.lfo) {
      try {
        this.droneNodes.lfo.stop();
        this.droneNodes.lfo.disconnect();
      } catch {}
    }
    this.droneNodes = { oscillators: [], noiseSource: null, lfo: null };
  }

  // ============================================================================
  // Procedural SFX Engines: Anvil Strike, Flame Ignite, Card Draw
  // ============================================================================

  public playAnvilStrike(): void {
    if (typeof window === 'undefined') return;
    const hasAudioContext = !!(window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
    if (!hasAudioContext) return;

    if (!this.ctx || !this.sfxMasterGain) {
      this.init().then(() => {
        if (this.ctx && this.sfxMasterGain) {
          this.playAnvilStrike();
        }
      });
      return;
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }

    const now = this.ctx.currentTime;

    // Voice 1: Metallic Clang Transient (Bandpassed Sawtooth 1400 Hz, Q=8)
    const clangOsc = this.ctx.createOscillator();
    clangOsc.type = 'sawtooth';
    clangOsc.frequency.setValueAtTime(1400, now);

    const clangFilter = this.ctx.createBiquadFilter();
    clangFilter.type = 'bandpass';
    clangFilter.frequency.setValueAtTime(1400, now);
    clangFilter.Q.setValueAtTime(8, now);

    const clangGain = this.ctx.createGain();
    clangGain.gain.setValueAtTime(0.0001, now);
    clangGain.gain.exponentialRampToValueAtTime(0.8, now + 0.005);
    clangGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);

    clangOsc.connect(clangFilter);
    clangFilter.connect(clangGain);
    clangGain.connect(this.sfxMasterGain);

    clangOsc.start(now);
    clangOsc.stop(now + 0.25);

    // Voice 2: Subterranean Iron Thud (Pitch Dive: 65 Hz -> 28 Hz)
    const thudOsc = this.ctx.createOscillator();
    thudOsc.type = 'sine';
    thudOsc.frequency.setValueAtTime(65, now);
    thudOsc.frequency.exponentialRampToValueAtTime(28, now + 0.65);

    const thudGain = this.ctx.createGain();
    thudGain.gain.setValueAtTime(0.0001, now);
    thudGain.gain.exponentialRampToValueAtTime(0.9, now + 0.01);
    thudGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.70);

    thudOsc.connect(thudGain);
    thudGain.connect(this.sfxMasterGain);

    thudOsc.start(now);
    thudOsc.stop(now + 0.75);

    // Voice 3: Resonant Bell Ringdown (520 Hz & 1040 Hz Cluster over 2.2s)
    [520, 1040].forEach((freq, idx) => {
      if (!this.ctx || !this.sfxMasterGain) return;
      const bellOsc = this.ctx.createOscillator();
      bellOsc.type = 'sine';
      bellOsc.frequency.setValueAtTime(freq, now);

      const bellGain = this.ctx.createGain();
      const initialVol = idx === 0 ? 0.4 : 0.2;
      bellGain.gain.setValueAtTime(0.0001, now);
      bellGain.gain.exponentialRampToValueAtTime(initialVol, now + 0.01);
      bellGain.gain.exponentialRampToValueAtTime(0.0001, now + 2.2);

      bellOsc.connect(bellGain);
      bellGain.connect(this.sfxMasterGain);

      bellOsc.start(now);
      bellOsc.stop(now + 2.25);
    });
  }

  public playFlameIgnite(): void {
    if (typeof window === 'undefined') return;
    const hasAudioContext = !!(window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
    if (!hasAudioContext) return;

    if (!this.ctx || !this.sfxMasterGain) {
      this.init().then(() => {
        if (this.ctx && this.sfxMasterGain) {
          this.playFlameIgnite();
        }
      });
      return;
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }

    const now = this.ctx.currentTime;
    const duration = 0.8;
    const buffer = this.ctx.createBuffer(1, Math.floor(this.ctx.sampleRate * duration), this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.exp(-3 * (i / data.length));
    }

    const noise = this.ctx.createBufferSource();
    noise.buffer = buffer;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(300, now);
    filter.frequency.exponentialRampToValueAtTime(2400, now + 0.2);
    filter.frequency.exponentialRampToValueAtTime(400, now + duration);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.45, now + 0.08);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.sfxMasterGain);

    noise.start(now);
    noise.stop(now + duration + 0.05);
  }

  public playCardDraw(): void {
    if (typeof window === 'undefined') return;
    const hasAudioContext = !!(window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
    if (!hasAudioContext) return;

    if (!this.ctx || !this.sfxMasterGain) {
      this.init().then(() => {
        if (this.ctx && this.sfxMasterGain) {
          this.playCardDraw();
        }
      });
      return;
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }

    const now = this.ctx.currentTime;
    const duration = 0.14;
    const buffer = this.ctx.createBuffer(1, Math.floor(this.ctx.sampleRate * duration), this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const noise = this.ctx.createBufferSource();
    noise.buffer = buffer;

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.setValueAtTime(1800, now);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.25, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.sfxMasterGain);

    noise.start(now);
    noise.stop(now + duration + 0.02);
  }

  public setMasterVolume(volume: number): void {
    if (!Number.isFinite(volume)) {
      if (volume === Infinity) volume = 1;
      else if (volume === -Infinity) volume = 0;
      else return; // Discard NaN and non-numeric inputs
    }
    this.masterVolume = Math.max(0, Math.min(1, volume));
    if (!this.ctx) return;

    const now = this.ctx.currentTime;
    if (this.sfxMasterGain) {
      this.sfxMasterGain.gain.setValueAtTime(this.masterVolume, now);
    }
    if (this.droneMasterGain && this.isPlaying && !this.isTransitioning) {
      const targetGain = Math.max(0.0001, 0.70 * this.masterVolume);
      this.droneMasterGain.gain.cancelScheduledValues(now);
      this.droneMasterGain.gain.setValueAtTime(
        Math.max(0.0001, this.droneMasterGain.gain.value),
        now
      );
      this.droneMasterGain.gain.exponentialRampToValueAtTime(targetGain, now + 0.3);
    }
    this.notifyListeners();
  }

  private handleVisibilityChange = (): void => {
    if (!this.ctx || !this.droneMasterGain || !this.isPlaying || this.isTransitioning) return;
    if (typeof document === 'undefined') return;

    const now = this.ctx.currentTime;
    if (document.hidden) {
      // Dim volume by 70% in background
      const dimmedGain = Math.max(0.0001, 0.20 * this.masterVolume);
      this.droneMasterGain.gain.cancelScheduledValues(now);
      this.droneMasterGain.gain.setValueAtTime(
        Math.max(0.0001, this.droneMasterGain.gain.value),
        now
      );
      this.droneMasterGain.gain.exponentialRampToValueAtTime(dimmedGain, now + 1.0);
    } else {
      // Restore volume on tab focus
      const normalGain = Math.max(0.0001, 0.70 * this.masterVolume);
      this.droneMasterGain.gain.cancelScheduledValues(now);
      this.droneMasterGain.gain.setValueAtTime(
        Math.max(0.0001, this.droneMasterGain.gain.value),
        now
      );
      this.droneMasterGain.gain.exponentialRampToValueAtTime(normalGain, now + 1.0);
    }
  };

  private generateBrownianNoiseBuffer(ctx: AudioContext, durationSeconds: number): AudioBuffer {
    const sampleRate = ctx.sampleRate;
    const bufferSize = sampleRate * durationSeconds;
    const buffer = ctx.createBuffer(2, bufferSize, sampleRate);

    for (let channel = 0; channel < 2; channel++) {
      const data = buffer.getChannelData(channel);
      let lastOut = 0.0;
      for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2.0 - 1.0;
        data[i] = (lastOut + 0.02 * white) / 1.02;
        lastOut = data[i];
        data[i] *= 3.5;
      }
    }
    return buffer;
  }

  private generateCatacombImpulseResponse(
    ctx: AudioContext,
    decaySeconds: number,
    decayRate: number
  ): AudioBuffer {
    const sampleRate = ctx.sampleRate;
    const length = Math.floor(sampleRate * decaySeconds);
    const impulse = ctx.createBuffer(2, length, sampleRate);

    for (let ch = 0; ch < 2; ch++) {
      const channelData = impulse.getChannelData(ch);
      for (let i = 0; i < length; i++) {
        const t = i / sampleRate;
        channelData[i] =
          (Math.random() * 2.0 - 1.0) * Math.pow(1 - t / decaySeconds, decayRate);
      }
    }
    return impulse;
  }

  public dispose(): void {
    if (this.stopTimeoutId !== null) {
      clearTimeout(this.stopTimeoutId);
      this.stopTimeoutId = null;
    }
    if (this.transitionTimeoutId !== null) {
      clearTimeout(this.transitionTimeoutId);
      this.transitionTimeoutId = null;
    }
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }
    this.teardownDroneNodes();
    if (this.ctx && this.ctx.state !== 'closed') {
      this.ctx.close().catch(() => {});
    }
    this.ctx = null;
    this.isInitialized = false;
    this.isPlaying = false;
    this.isTransitioning = false;
    this.listeners.clear();
  }
}

export const audioEngine = AudioEngine.getInstance();
